import { ColumnSource, RowSource } from "@omegagrid/grid";
import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { TableDefinition, SqlColumnDefinition } from "@reporting/rp-types/mdm";
import { SQL_TABLE_DEF_DEFAULTS } from "../types";

export class SqlColumnsDefGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	constructor (
		private table: TableDefinition
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 2,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red
			}, {
				action: 'duplicate',
				title: msg('Duplicate'),
				icon: 'copy',
				color: ColorTypes.Yellow
			}],
			filter: false,
			sorting: false,
		}, {
			data: 'column_name',
			header: msg('Name'),
			t: 't',
			e: true,
			w: 250
		}, {
			data: 'data_type',
			header: msg('Datatype'),
			t: 't',
			e: true,
			w: 250
		}, {
			data: 'is_nullable',
			header: msg('Nullable'),
			t: 'b',
			e: true,
			w: 90,
		}, {
			data: 'column_default',
			header: msg('Default'),
			t: 't',
			e: true,
			w: 250,

		}, {
			data: 'column_formula',
			header: msg('Formula'),
			t: 't',
			e: true,
			w: 250
		}, {
			data: 'orig_column_name',
			t: 't',
			e: false,
			hidden: true
		}];
		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		const columnDefs = await rp.ms('mdm').get<SqlColumnDefinition[]>(`columnDefinitions/${this.table.id_md}`);
		const rows = columnDefs.length ? columnDefs.map(data => ({ data })) : SQL_TABLE_DEF_DEFAULTS.map(data => ({ data }));
		return rows;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			refreshAfterSave: true,
			filters: true,
			saveMode: 'all_rows',
			pageSize: null,
			toolbar: {
				refresh: { enabled: false },
			},
			showEmptyRow: true,
			rowMenu: {
				addRows: true,
				deleteRows: true,
			},
		});
	}

	async validate(data: Record<string, unknown>[]): Promise<string | null> {
		const duplicates = [];
		const seen = new Set<string>();
		for (const row of data) {
			if (!row.column_name || !row.data_type)
				return 'Name and datatype cannot be empty!';
			if ((row.column_name as unknown as string) !== 'IDZ' && !row.is_nullable && !row.column_default)
				return 'Column must be nullable or have a default value!';
			const key = `${row.column_name}`;
			if (seen.has(key)) duplicates.push(row);
			else seen.add(key);
		}
		if (duplicates.length > 0)
			return 'Names must be unique!';
		// check if primary key column exists... if not, ask user if they want to continue without it
		const idzColumn = data.find(row => row.column_name === 'IDZ');
		if (!idzColumn) {
			const command = await rp.host.openMenu({
				content: 'yes_no',
				text: `${msg('Do you want to continue without primary key column?')}`
			});
			if (command?.id == 'N')
				return 'Operation cancelled by user.';
		} else {
			if (idzColumn.data_type !== 'bigint')
				return 'Primary key column must be of type "bigint"!';
		}
		// validation passed
		return null;
	}

	async save(rowData: RowData[]) {
		const data = rowData.filter(row => row.state !== 'deleted').map(row => row.data);
		const validationError = await this.validate(data);
		if (validationError) {
			rp.host.bucket.showAlert({content: msg(validationError), type: 'warning'});
			return Promise.reject(validationError);
		}
		await rp.ms('mdm').post(`sqlTable/${this.table.id_md}`, data);
	}
}
